import { Button, Form, Modal } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { RootState } from '../../../../rootReducer';
import { AppDispatch } from '../../../../store';
import { FindingTypeDetails, FindingTypeRuleDetails } from '../../AnnotationStandardForm/AnnotationStandardForm.slice';
import FindingTypeSelect from '../AnnotationForm/Annotation/FindingTypeSelect';
import { getFindingTypeProperties, setShowMultiAnnotationModal, updateMultiAnnotations } from '../AnnotationForm/AnnotationForm.slice';
import MultiselectProperties from './MultiselectProperties';
import { areAllPropertyValuesSame, getPropertyInitialValue, getUniqueFindingType } from './utils';
import styles from './AnnotationMultiselectModal.module.scss';
import { CreateAnnotationProperty } from '../AnnotationForm/AnnotationForm.types';

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const AnnotationMultiselectModal = ({
  showModal,
  multipleSelectedAnnotationsForEdit,
  annotationStandard,
  selectedProperties,
  isLoading,
  onHideModal,
  onGetFindingTypeProperties,
  onSave
}: Props) => {
  const [selectedFindingType, setSelectedFindingType] = useState<FindingTypeDetails | null>(null);
  const [properties, setProperties] = useState<CreateAnnotationProperty[]>([]);
  const [selectedRules, setSelectedRules] = useState<FindingTypeRuleDetails[]>([]);
  const [comment, setComment] = useState('');

  const onChange = useCallback(
    (id: string) => {
      const selectedType = annotationStandard!.findingTypes.find(x => x.id === id)!;
      setSelectedFindingType(selectedType);
      setSelectedRules(selectedType.rules);
      onGetFindingTypeProperties(selectedType.id!, selectedType.findingTypeTemplate!.id);
    },
    [annotationStandard, onGetFindingTypeProperties]
  );

  const onSaveButtonClicked = useCallback(() => {
    Modal.confirm({
      title: 'Confirm',
      content: 'Are you sure you want to update findings?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        onSave(selectedFindingType!.findingTypeTemplate!.name, comment, properties);
      }
    });
  }, [comment, onSave, properties, selectedFindingType]);

  useEffect(() => {
    if (annotationStandard && multipleSelectedAnnotationsForEdit) {
      const uniqueFindingTypeId = getUniqueFindingType(multipleSelectedAnnotationsForEdit);
      if (uniqueFindingTypeId) {
        onChange(uniqueFindingTypeId);
      }
    }
  }, [annotationStandard, multipleSelectedAnnotationsForEdit, onChange]);

  useEffect(() => {
    if (!(selectedProperties && selectedProperties.length) || !selectedFindingType) {
      return;
    }

    const areSame = areAllPropertyValuesSame(selectedProperties, multipleSelectedAnnotationsForEdit);

    const propertiesForm: CreateAnnotationProperty[] = selectedProperties
      .filter(property => {
        return selectedFindingType.representations.find(x => x.findingTypePropertyId === property.id);
      })
      .map(property => {
        let isRequired = true;
        const representation = selectedFindingType.representations.find(x => x.findingTypePropertyId === property.id);
        if (representation) {
          isRequired = representation.isMandatory;
        }

        let value = getPropertyInitialValue(areSame, property, multipleSelectedAnnotationsForEdit);
        let prop: CreateAnnotationProperty = {
          findingTypePropertyId: property.id,
          name: property.name,
          value: representation?.group !== 'Localization' ? value : '',
          isReadonly: false,
          label: property.fullName,
          required: isRequired
        };

        return prop;
      });

    setProperties(propertiesForm);
  }, [multipleSelectedAnnotationsForEdit, selectedFindingType, selectedProperties]);

  const saveDisabled = false;

  return (
    <Modal
      title='Edit multiple findings'
      wrapClassName='multi-annotation-modal'
      className='modal-container'
      visible={showModal}
      onCancel={() => onHideModal(false)}
      footer={null}
    >
      <Form className={styles.multiAnnotationFormContainer} layout='vertical'>
        {annotationStandard && (
          <FindingTypeSelect
            disabled={false}
            selectedFindingType={selectedFindingType}
            findingTypes={annotationStandard.findingTypes}
            onChange={onChange}
          />
        )}
        <MultiselectProperties {...{ annotationStandard, properties, selectedFindingType, selectedProperties, selectedRules, setProperties }} />
        <Form.Item className={styles.antFormItem} label={'Comment'}>
          <TextArea className={styles.inputStyles} placeholder='Enter Value' value={comment} onChange={e => setComment(e.target.value)} />
        </Form.Item>
        <div className={styles.annotationFormFooter}>
          <Button loading={isLoading} disabled={saveDisabled || isLoading} key='submit' htmlType='submit' onClick={onSaveButtonClicked}>
            Save
          </Button>
          <Button disabled={isLoading} style={{ marginLeft: '1rem', marginRight: '5px' }} key='back' onClick={() => onHideModal(false)}>
            Cancel
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  showModal: state.annotationForm.showMultiAnnotationModal,
  multipleSelectedAnnotationsForEdit: state.annotationForm.multipleSelectedAnnotationsForEdit,
  annotationStandard: state.annotation.annotationStandardDetails,
  selectedProperties: state.annotationForm.selectedProperties,
  isLoading: state.annotationForm.submittingForm
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onHideModal: (show: boolean) => dispatch(setShowMultiAnnotationModal(show)),
  onGetFindingTypeProperties: (id: string, templateId: string) => dispatch(getFindingTypeProperties(id, templateId)),
  onSave: (name: string, message: string, properties: CreateAnnotationProperty[]) => dispatch(updateMultiAnnotations(name, message, properties))
});

export default connect(mapStateToProps, mapDispatchToProps)(AnnotationMultiselectModal);
